import React, {
  useEffect, useState, useCallback, useMemo,
} from 'react';
import { GoogleMap, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Activity } from '../../store/Activities.store';
import {
  ActivityInfoContainer, ActivityTitle, ActivityTitleContainer, InfoActivityContainer, InfoItem, InfoText, MapContainer, MedalRequestContainer, MedalRequestInfo, RegisterButton, RegisterButtonText, Separator,
  CarouselContainer, CarouselContent, CarouselArrowLeft, CarouselArrowRight, CarouselIndicator, IndicatorDot,
} from './styles';
import { formatDuration } from '../../utils';
import theme, { appTheme } from '../../theme';
import mapStyle from '../ActivityDetail/mapStyle';
import api from '../../services/api';
import MedalRequestModal from '../MedalRequest';
import { UserVirtualRaceType } from '../../types/VirtualRace';
import { MedalRequestType } from '../../types/Challenger';
import coreApi from '../../services/coreApi';

interface Props {
  activities: Activity [];
  isValidated?: boolean;
  userId: number;
  userVirtualRace: UserVirtualRaceType;
  hasMedalRequest?: boolean;
  customFinishedMessage?: string;
}

export default function VirtualRaceActivity({
  activities, isValidated = false, userVirtualRace, userId, hasMedalRequest, customFinishedMessage,
}: Props) {
  const { isLoaded: mapIsLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API || '',
  });

  const [coordsMap, setCoordsMap] = useState<Map<number, any>>(new Map());
  const [mapsMap, setMapsMap] = useState<Map<number, any>>(new Map());
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const [medalRequestModal, setMedalRequestModal] = useState<boolean>(false);
  const [medalRequest, setMedalRequest] = useState<MedalRequestType>();
  const [medalLoading, setMedalLoading] = useState<boolean>(false);

  const sortedActivities = useMemo(() => {
    if (!activities || activities.length === 0) return [];
    return [...activities].sort((a, b) => (b.distance || 0) - (a.distance || 0));
  }, [activities]);

  const showArrows = sortedActivities.length > 1;

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : sortedActivities.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < sortedActivities.length - 1 ? prev + 1 : 0));
  };

  const onMapLoad = useCallback((activityId: number, mapInstance: any) => {
    setMapsMap((prev) => new Map(prev).set(activityId, mapInstance));
  }, []);

  const fitBoundsToPolyline = useCallback((activityId: number, coords: any) => {
    const map = mapsMap.get(activityId);
    if (map && coords && coords.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      coords.forEach((coord: any) => {
        bounds.extend({ lat: coord.latitude, lng: coord.longitude });
      });
      map.fitBounds(bounds, {
        top: 50, bottom: 50, left: 50, right: 50,
      });
    }
  }, [mapsMap]);

  const loadCoords = async (activity: Activity) => {
    if (activity?.id && activity?.summary_polyline) {
      await api.get(`/user_activities/coordinates/${activity?.id}`)
        .then((response) => {
          setCoordsMap((prev) => new Map(prev).set(activity.id, response.data));
        })
        .catch(() => {
          // Erro ao carregar coordenadas
        });
    }
  };

  const getMedalRequest = () => {
    setMedalLoading(true);
    if (hasMedalRequest) {
      coreApi.get(`medal-request/user/${userId}/${userVirtualRace.slug}`)
        .then((response) => {
          if (response.data) {
            setMedalRequest(response.data);
          }
          setMedalLoading(false);
        }).catch(() => {
          setMedalLoading(false);
        });
    }
  };

  useEffect(() => {
    if (sortedActivities && sortedActivities.length > 0) {
      sortedActivities.forEach((activity) => {
        loadCoords(activity);
      });
    }
  }, [sortedActivities]);

  useEffect(() => {
    coordsMap.forEach((coords, activityId) => {
      fitBoundsToPolyline(activityId, coords);
    });
  }, [coordsMap, fitBoundsToPolyline]);

  useEffect(() => {
    getMedalRequest();
  }, []);

  return (

    <>
      <MedalRequestModal challenge={userVirtualRace} isVisible={medalRequestModal} setVisible={setMedalRequestModal} user_id={userId} refresh={getMedalRequest} />
      <ActivityTitleContainer isValidated={isValidated}>
        <ActivityTitle>Parabéns, você concluiu sua corrida!</ActivityTitle>
      </ActivityTitleContainer>
      {sortedActivities && sortedActivities.length > 0 && (
        <>
          <CarouselContainer>
            {showArrows && (
              <CarouselArrowLeft onClick={handlePrevious}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </CarouselArrowLeft>
            )}
            <CarouselContent>
              {(() => {
                const activity = sortedActivities[currentIndex];
                const coords = coordsMap.get(activity.id);
                return (
                  <InfoActivityContainer key={activity.id}>
                    <Separator />
                    {
                      coords && coords.length > 0 && (
                        (mapIsLoaded && (
                          <MapContainer>
                            <GoogleMap
                              mapContainerStyle={{
                                width: '100%',
                                height: '100%',
                              }}
                              center={{
                                lat: coords[0].latitude,
                                lng: coords[0].longitude,
                              }}
                              zoom={13}
                              onLoad={(mapInstance) => onMapLoad(activity.id, mapInstance)}
                              options={{
                                disableDefaultUI: true,
                                styles: mapStyle,
                              }}
                            >
                              <Polyline
                                path={coords.map((coord: { latitude: any; longitude: any; }) => ({ lat: coord.latitude, lng: coord.longitude }))}
                                options={{
                                  strokeColor: appTheme.primaryD3,
                                  strokeOpacity: 1,
                                  strokeWeight: 3,
                                }}
                              />
                            </GoogleMap>
                          </MapContainer>
                        ))
                      )
                    }
                    {activity?.distance ? (
                      <>
                        <InfoItem>
                          <InfoText>Distância</InfoText>
                          <InfoText>
                            {`${activity?.distance} km`}
                          </InfoText>
                        </InfoItem>
                        <Separator />
                      </>
                    ) : <></>}
                    {activity?.pace ? (
                      <>
                        <InfoItem>
                          <InfoText>Ritmo</InfoText>
                          <InfoText>
                            {
                              `${formatDuration(activity?.pace, 'pace')} /km`
                            }
                          </InfoText>
                        </InfoItem>
                        <Separator />
                      </>
                    ) : <></>}
                    {activity?.duration ? (
                      <>
                        <InfoItem>
                          <InfoText>Tempo</InfoText>
                          <InfoText>
                            {
                              `${formatDuration(activity?.duration as number, 'time')}`
                            }
                          </InfoText>
                        </InfoItem>
                        <Separator />
                      </>
                    ) : <></>}
                    {activity?.start_date ? (
                      <>
                        <InfoItem>
                          <InfoText>Data</InfoText>
                          <InfoText>
                            {
                              `${format(parseISO(activity.start_date), 'dd/MM/yyyy', { locale: ptBR })}`
                            }
                          </InfoText>
                        </InfoItem>
                        <Separator />
                      </>
                    ) : <></>}
                  </InfoActivityContainer>
                );
              })()}
            </CarouselContent>
            {showArrows && (
              <CarouselArrowRight onClick={handleNext}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </CarouselArrowRight>
            )}
          </CarouselContainer>
          {showArrows && (
            <CarouselIndicator>
              {sortedActivities.map((_, index) => (
                <IndicatorDot key={index} active={index === currentIndex} />
              ))}
            </CarouselIndicator>
          )}
        </>
      )}
      {!isValidated && (
        <ActivityInfoContainer isValidated={isValidated}>
          <ActivityTitle>Sua atividade está sendo validada</ActivityTitle>
        </ActivityInfoContainer>
      )}
      {isValidated && (
        <ActivityInfoContainer isValidated={isValidated}>
          {hasMedalRequest && (
            <>
              {!medalRequest && !medalLoading && (
                <RegisterButton onClick={() => setMedalRequestModal(true)} color="white">
                  <RegisterButtonText color={theme.colors.primary}>Solicite sua medalha</RegisterButtonText>
                </RegisterButton>
              )}
              {medalRequest && medalRequest?.status === 'SENT' && !medalLoading && (
                <MedalRequestContainer>
                  <MedalRequestInfo>Medalha enviada.</MedalRequestInfo>
                  <MedalRequestInfo>Confira em seu e-mail o link de rastreio da remessa.</MedalRequestInfo>
                </MedalRequestContainer>
              )}
              {medalRequest && medalRequest?.status === 'REQUESTED' && !medalLoading && (
                <MedalRequestContainer>
                  <MedalRequestInfo>Medalha em preparação para envio.</MedalRequestInfo>
                  <MedalRequestInfo>Você receberá o link de rastreio em breve no seu e-mail.</MedalRequestInfo>
                </MedalRequestContainer>
              )}
            </>
          )}
          {!hasMedalRequest && customFinishedMessage && (
            <MedalRequestContainer>
              <MedalRequestInfo>{customFinishedMessage}</MedalRequestInfo>
            </MedalRequestContainer>
          )}
        </ActivityInfoContainer>
      )}
    </>
  );
}
